<script lang="ts">
  import { onMount } from "svelte";
  import { fly } from "svelte/transition";
  import autoAnimate from "@formkit/auto-animate";
  import Separator from "$components/+blocks/Separator/Separator.svelte";
  import Avatar from "$components/+blocks/Avatar/Avatar.svelte";
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import LoadingAtom from "$components/+blocks/Loading/LoadingAtom.svelte";
  import { communitySwitcherHomeUrl } from "$utils/urls";
  import { LEFT_SIDEPANEL_WIDTH_PX } from "$utils/consts";
  import { getModalContext } from "./forms/CreateCommunityModal.svelte";
  import { communities, communitySlug, isCommunitiesListFetched } from "$stores/community";
  import { Check, Plus, Search } from "$components/+blocks/OVIcon";
  import { canCreateCommunity } from "$stores/globalPermissions";

  export let isWorkspaceSwitcherOpen: boolean;

  const { open: openCreateCommunityModal } = getModalContext();
  let filter: string = "";
  let inputElement: HTMLInputElement | null = null;

  $: if (inputElement) inputElement.focus();

  // Filter communities by user filter input.
  // If the filter input is a substring of the community name array return the community otherwise return empty array.
  // Because we are flattening the array only non-empty arrays will be returned.
  $: filteredCommunities = $communities.communityList.flatMap((community) => {
    if (!community) return [];
    const nameArray = community.name?.split(" ");
    if (!nameArray) return [];

    for (let i = 0; i < nameArray.length; i++) {
      if (nameArray[i].toLowerCase().startsWith(filter.toLowerCase())) {
        return community;
      }
    }

    return [];
  });

  onMount(() => {
    if ($communities.communityList.length === 0) {
      communities.fetch();
    }
  });
</script>

<div
  class="firefox:bg-neutral-200 firefox:dark:bg-neutral-700 max-w-[calc(100vw-16px)] overscroll-contain rounded-lg border-[0.5px] border-black/10
  glossy-effect pt-1 shadow-[0_0_9px_1px_rgba(0,0,0,0.13)] dark:border-white/10"
  style="width: {LEFT_SIDEPANEL_WIDTH_PX}px"
  in:fly={{ y: -8, duration: 60 }}
>
  {#if $communities.communityList.length > 6}
    <div class="flex w-full items-center gap-2 px-3.5">
      <div class="flex aspect-square w-[24px] items-center justify-center">
        <OVIcon icon={Search} class="text-neutral-500 dark:text-neutral-300" size="size-3.5" />
      </div>

      <input
        bind:this={inputElement}
        bind:value={filter}
        placeholder="Search workspaces"
        class="my-2 border-0 bg-transparent text-[13px] text-neutral-500 placeholder-neutral-500 focus:outline-none
        dark:text-neutral-300 dark:placeholder-neutral-400"
      />
    </div>

    <Separator class="mx-3 my-1 bg-neutral-300 dark:bg-neutral-300/10" />
  {/if}

  <div
    class="flex scrollbar-thin max-h-[368px] flex-col gap-0.5 overflow-x-hidden overflow-overlay overflow-y-auto
    overscroll-contain scrollbar-invisible px-1 scrollbar-glossy-light hover:scrollbar-visible dark:scrollbar-glossy-dark"
    use:autoAnimate={{ duration: 200 }}
  >
    {#if $isCommunitiesListFetched}
      {#each filteredCommunities as community (community.slug)}
        <a
          href={community.slug && communitySwitcherHomeUrl({ communitySlug: community.slug })}
          class="relative flex items-center gap-2 rounded p-2 hover:bg-neutral-400/20 dark:hover:bg-white/10"
          on:click={() => (isWorkspaceSwitcherOpen = false)}
        >
          <Avatar
            mainImage={community.logoImage}
            backgroundColor={community.primaryColor}
            wrapperClasses={"shadow-md bg-neutral-400 dark:bg-neutral-500"}
            size="size-[28px]"
            radius="rounded-md"
            label={community.name?.[0]}
            textSize="text-xs"
            cropPosition={null}
          />
          <span
            class="line-clamp-1 text-left text-[13px] leading-[16px] font-normal text-neutral-600 dark:text-neutral-300"
          >
            {community.name}
          </span>

          {#if community.slug === $communitySlug}
            <OVIcon icon={Check} class="ml-auto text-emerald-500" />
          {/if}

          <span class="sr-only">{community.name}</span>
        </a>
      {/each}
    {:else}
      {#each [...Array(3)] as _}
        <div class="flex items-center space-x-2.5 px-2 py-1">
          <LoadingAtom size={"h-[24px] aspect-square"} />
          <div class="my-2 flex flex-col gap-2">
            <LoadingAtom size={"h-2 w-16"} />
            <LoadingAtom size={"h-2 w-16"} />
          </div>
        </div>
      {/each}
    {/if}
  </div>

  {#if $canCreateCommunity}
    {#if filteredCommunities.length > 0}
      <Separator class="mx-3 my-1 bg-neutral-300 dark:bg-neutral-300/10" />
    {/if}
    <div class="mb-1 flex flex-col">
      <button
        class="mx-1 flex items-center gap-2 rounded px-2 py-2 text-xs font-medium text-neutral-500 hover:bg-neutral-400/20 dark:text-neutral-100 dark:hover:bg-white/10"
        on:click={() => {
          $openCreateCommunityModal = true;
          isWorkspaceSwitcherOpen = false;
        }}
      >
        <span
          class="mr-0.5 flex size-[28px] items-center justify-center rounded-md bg-neutral-300 dark:bg-neutral-600"
        >
          <OVIcon icon={Plus} />
        </span>
        Create workspace
      </button>
    </div>
  {/if}
</div>
